import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
/**
 * MachinesPanel — the machines and panels installed on a project.
 *
 * A machine is matched to paperwork by its tag (MCC-01, DB-03…), the same
 * text engineers already type into a checklist's "Panel / Installation
 * Ref". Tags found in paperwork but not yet registered are offered for
 * adoption in one click, so nothing has to be typed twice.
 *
 * Each machine carries a QR sticker; scanning it opens a redacted public
 * page that lists the machine's completed checklists.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from './ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Camera, ChevronDown, ChevronUp, ClipboardCheck, Cpu, FileSpreadsheet, Pencil, Plus, Printer, QrCode, Trash2, Wand2 } from 'lucide-react';
import { exportView } from '../lib/exportXlsx';
import { QrLabelDialog } from './QrLabelDialog';
import EntityPhotoGallery from './EntityPhotoGallery';
import useDeepShortcut from '../hooks/useDeepShortcut';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = [
  { value: 'planned', label: i18n.t('auto.lbl.planned') },
  { value: 'installed', label: i18n.t('auto.lbl.installed') },
  { value: 'commissioned', label: i18n.t('auto.lbl.commissioned') },
  { value: 'handed_over', label: i18n.t('auto.lbl.handedOver') },
  { value: 'decommissioned', label: i18n.t('auto.lbl.decommissioned') },
];
const STATUS_TONE = {
  planned: 'bg-slate-100 text-slate-700',
  installed: 'bg-blue-100 text-blue-700',
  commissioned: 'bg-green-100 text-green-700',
  handed_over: 'bg-emerald-100 text-emerald-700',
  decommissioned: 'bg-amber-100 text-amber-800',
};

const EMPTY = {
  tag: '', name: '', system_ref: '', manufacturer: '', model: '',
  serial_number: '', location: '', description: '', status: 'planned',
  checklist_template_id: '',
};

const MachineForm = ({ form, setForm, templates, tagPrefix }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
    <div>
      <Label className="text-xs">{i18n.t('auto.misc.tag')}</Label>
      <Input value={form.tag} onChange={(e) => setForm({ ...form, tag: e.target.value })}
        placeholder={i18n.t('auto.misc.mcc01')} className="h-9" data-testid="machine-tag-input" />
      <p className="text-[11px] text-slate-500 mt-1">
        {i18n.t('auto.misc.mustMatchThePanelReferenceUsed')}
        {tagPrefix ? ` Pre-filled with the project number (${tagPrefix}) — edit or clear it freely.` : ''}
      </p>
    </div>
    <div>
      <Label className="text-xs">{i18n.t('auto.misc.name')}</Label>
      <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
        placeholder={i18n.t('auto.misc.fillingLineMcc')} className="h-9" data-testid="machine-name-input" />
    </div>
    <div>
      <Label className="text-xs">{i18n.t('auto.misc.system')}</Label>
      <Input value={form.system_ref} onChange={(e) => setForm({ ...form, system_ref: e.target.value })}
        placeholder={i18n.t('auto.misc.fillingLine3')} className="h-9" data-testid="machine-system-input" />
    </div>
    <div>
      <Label className="text-xs">{i18n.t('auto.misc.status')}</Label>
      <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
        <SelectTrigger className="h-9" data-testid="machine-status-select"><SelectValue /></SelectTrigger>
        <SelectContent>
          {STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
    <div>
      <Label className="text-xs">{i18n.t('auto.misc.manufacturer')}</Label>
      <Input value={form.manufacturer} onChange={(e) => setForm({ ...form, manufacturer: e.target.value })}
        className="h-9" data-testid="machine-manufacturer-input" />
    </div>
    <div>
      <Label className="text-xs">{i18n.t('auto.misc.model')}</Label>
      <Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })}
        className="h-9" data-testid="machine-model-input" />
    </div>
    <div>
      <Label className="text-xs">{i18n.t('auto.misc.serialNumber')}</Label>
      <Input value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value })}
        className="h-9" data-testid="machine-serial-input" />
    </div>
    <div>
      <Label className="text-xs">{i18n.t('auto.misc.locationOnSite')}</Label>
      <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
        placeholder={i18n.t('auto.misc.hallBWestWall')} className="h-9" data-testid="machine-location-input" />
    </div>
    <div className="sm:col-span-2">
      <Label className="text-xs">{i18n.t('auto.misc.routineChecklist')}</Label>
      <Select value={form.checklist_template_id || 'none'}
        onValueChange={(v) => setForm({ ...form, checklist_template_id: v === 'none' ? '' : v })}>
        <SelectTrigger className="h-9" data-testid="machine-template-select">
          <SelectValue placeholder={i18n.t('auto.misc.noRoutineChecklist')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">{i18n.t('auto.misc.noRoutineChecklist')}</SelectItem>
          {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
        </SelectContent>
      </Select>
      <p className="text-[11px] text-slate-500 mt-1">
        Staff can start a new completion from the machine card; each one is logged as a dated entry.
      </p>
    </div>
    <div className="sm:col-span-2">
      <Label className="text-xs">{i18n.t('auto.misc.description')}</Label>
      <Textarea rows={2} value={form.description}
        onChange={(e) => setForm({ ...form, description: e.target.value })}
        data-testid="machine-description-input" />
    </div>
  </div>
);

const MachinesPanel = ({ projectId, isAdmin = false, canEdit = true, projectNumber = '', focusMachineId, onOpenChecklist }) => {
  const { t } = useTranslation();
  const [machines, setMachines] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [deleteId, setDeleteId] = useState(null);
  const [qrTarget, setQrTarget] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const [expanded, setExpanded] = useState(focusMachineId || null);
  const [checks, setChecks] = useState({});

  // Panel tags are expected to start with the project number, so the
  // create form opens with it already typed in.
  const tagPrefix = projectNumber ? `${projectNumber}-` : '';
  const blankForm = () => ({ ...EMPTY, tag: tagPrefix });

  const load = useCallback(async () => {
    try {
      const [m, s, t] = await Promise.all([
        axios.get(`${API}/machines`, { params: { project_id: projectId } }),
        axios.get(`${API}/projects/${projectId}/panel-refs`),
        axios.get(`${API}/checklists/templates`),
      ]);
      setMachines(m.data || []);
      setSuggestions(s.data || []);
      setTemplates(t.data || []);
    } catch { toast.error(t('auto.misc.couldNotLoadMachines')); }
    setLoading(false);
  }, [projectId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (focusMachineId) setExpanded(focusMachineId); }, [focusMachineId]);

  // Dashboard shortcuts: pin the panel/machine the user has open.
  useDeepShortcut({
    active: !!expanded,
    tab: 'projects',
    context: { openProjectId: projectId, projectTab: 'machines', focusMachineId: expanded },
    label: machines.find((m) => m.id === expanded)?.tag || 'Machine',
    subtitle: 'Machines',
    icon: 'machines',
    perms: ['projects.view'],
    priority: 3,
    focusKey: 'focusMachineId',
    onFocus: (id) => setExpanded(id),
  });

  const loadChecks = useCallback(async (mid) => {
    try {
      const r = await axios.get(`${API}/machines/${mid}/checks`);
      setChecks((p) => ({ ...p, [mid]: r.data || [] }));
    } catch { /* history is a nicety */ }
  }, []);

  useEffect(() => { if (expanded) loadChecks(expanded); }, [expanded, loadChecks]);

  const newCheck = async (m) => {
    try {
      const r = await axios.post(`${API}/machines/${m.id}/checks`);
      toast.success(`${r.data.number} created — opening it`);
      loadChecks(m.id);
      onOpenChecklist?.(r.data.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not start the check'); }
  };

  const setMainPhoto = async (mid, photoId) => {
    try {
      await axios.put(`${API}/machines/${mid}/main-photo`, { photo_id: photoId });
      setMachines((p) => p.map((x) => (x.id === mid ? { ...x, main_photo_id: photoId } : x)));
      toast.success(photoId ? t('auto.misc.publicPhotoSet') : t('auto.misc.publicPhotoRemoved'));
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not set the public photo'); }
  };

  const save = async () => {
    if (!form.tag.trim()) { toast.error(t('auto.misc.aTagIsRequired')); return; }
    if (tagPrefix && form.tag.trim() === tagPrefix.trim()) {
      toast.error(t('auto.misc.addThePanelTagAfterThe'));
      return;
    }
    try {
      if (editing) {
        await axios.put(`${API}/machines/${editing.id}`, form);
        toast.success(t('auto.misc.machineUpdated'));
      } else {
        await axios.post(`${API}/machines`, { ...form, project_id: projectId });
        toast.success(t('auto.misc.machineAdded'));
      }
      setCreateOpen(false); setEditing(null); setForm(EMPTY);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save the machine'); }
  };

  const adopt = async (tag) => {
    try {
      await axios.post(`${API}/machines`, { ...EMPTY, tag, project_id: projectId, status: 'installed' });
      toast.success(`${tag} registered`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not register the tag'); }
  };

  const remove = async () => {
    try {
      await axios.delete(`${API}/machines/${deleteId}`);
      toast.success(t('auto.misc.machineDeleted'));
      setDeleteId(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not delete'); }
  };

  const toggle = (id) => setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const exportMachines = () => exportView({
    filename: projectNumber ? `machines-${projectNumber}` : 'machines',
    sheetName: 'Machines', rows: machines,
    columns: [
      { header: t('auto.lbl.tag'), key: 'tag' },
      { header: t('auto.lbl.name'), key: 'name' },
      { header: t('auto.lbl.system'), key: 'system_ref' },
      { header: t('auto.lbl.status'), value: (m) => STATUSES.find((s) => s.value === m.status)?.label || m.status },
      { header: t('auto.lbl.manufacturer'), key: 'manufacturer' },
      { header: t('auto.lbl.model'), key: 'model' },
      { header: t('auto.lbl.serialNumber'), key: 'serial_number' },
      { header: t('auto.lbl.location'), key: 'location' },
      { header: t('auto.lbl.routineChecklist'), key: 'checklist_template_name' },
      { header: t('auto.lbl.description'), key: 'description' },
    ],
  });

  return (
    <div className="space-y-4" data-testid="machines-panel">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
            <Cpu className="w-4 h-4 text-[#AA0000]" /> Machines &amp; Panels
          </h3>
          <p className="text-xs text-slate-500">
            Each machine gets a QR sticker that opens its record — and its signed checklists — on any phone.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {machines.length > 0 && (
            <Button variant="outline" size="sm" onClick={exportMachines} data-testid="machines-export-btn">
              <FileSpreadsheet className="w-4 h-4 mr-2" /> {t('auto.misc.excel')}
            </Button>
          )}
          {selected.length > 0 && (
            <Button variant="outline" size="sm" onClick={() => setBulkOpen(true)} data-testid="machines-bulk-labels-btn">
              <Printer className="w-4 h-4 mr-2" /> Labels ({selected.length})
            </Button>
          )}
          {canEdit && (
            <Dialog open={createOpen} onOpenChange={(o) => { setCreateOpen(o); if (o) { setEditing(null); setForm(blankForm()); } }}>
              <DialogTrigger asChild>
                <Button size="sm" data-testid="add-machine-btn"><Plus className="w-4 h-4 mr-2" /> {t('auto.misc.addMachine')}</Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
                <DialogHeader><DialogTitle>{t('auto.misc.addMachinePanel')}</DialogTitle></DialogHeader>
                <MachineForm form={form} setForm={setForm} templates={templates} tagPrefix={tagPrefix} />
                <DialogFooter>
                  <Button variant="outline" onClick={() => setCreateOpen(false)}>{t('auto.misc.cancel')}</Button>
                  <Button onClick={save} data-testid="save-machine-btn">{t('auto.misc.addMachine')}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {suggestions.length > 0 && canEdit && (
        <Card className="border-amber-200 bg-amber-50/60">
          <CardContent className="p-3">
            <p className="text-xs text-amber-900 flex items-center gap-1.5 mb-2">
              <Wand2 className="w-3.5 h-3.5" />
              Panel references found in this project&apos;s paperwork but not registered yet:
            </p>
            <div className="flex flex-wrap gap-2">
              {suggestions.map((s) => (
                <button key={s.tag} type="button" onClick={() => adopt(s.tag)}
                  data-testid={`adopt-tag-${s.tag}`}
                  className="text-xs px-2 py-1 rounded border border-amber-300 bg-white hover:bg-amber-100 transition-colors">
                  {s.tag} <span className="text-amber-700">+{s.documents}</span>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <p className="text-sm text-slate-500">{t('auto.misc.loading')}</p>
      ) : machines.length === 0 ? (
        <div className="text-center py-8 text-sm text-slate-500" data-testid="no-machines">
          {t('auto.misc.noMachinesRegisteredOnThisProject')}
        </div>
      ) : (
        <div className="space-y-2">
          {machines.map((m) => (
            <Card key={m.id} data-testid={`machine-card-${m.tag}`}>
              <CardContent className="p-3">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                  <div className="flex items-start gap-3 min-w-0 w-full sm:flex-1">
                    <input type="checkbox" checked={selected.includes(m.id)} onChange={() => toggle(m.id)}
                      className="mt-1 shrink-0" data-testid={`select-machine-${m.tag}`} />
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold text-slate-800 break-words">{m.tag}</span>
                        <Badge className={`text-[11px] ${STATUS_TONE[m.status] || STATUS_TONE.planned}`}>
                          {STATUSES.find((s) => s.value === m.status)?.label || m.status}
                        </Badge>
                        {m.public_enabled === false && (
                          <Badge variant="outline" className="text-[11px] text-slate-500">{t('auto.misc.qrOff')}</Badge>
                        )}
                      </div>
                      {m.name && <p className="text-sm text-slate-700 break-words">{m.name}</p>}
                      <p className="text-xs text-slate-500 break-words">
                        {[m.system_ref && `System: ${m.system_ref}`,
                          m.manufacturer && [m.manufacturer, m.model].filter(Boolean).join(' '),
                          m.serial_number && `S/N ${m.serial_number}`,
                          m.location].filter(Boolean).join(' · ')}
                      </p>
                      {m.checklist_template_name && (
                        <p className="text-xs text-slate-600 mt-0.5 flex items-center gap-1">
                          <ClipboardCheck className="w-3 h-3 text-[#AA0000]" />
                          Routine: {m.checklist_template_name}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap justify-end gap-1 w-full sm:w-auto sm:shrink-0">
                    <Button variant="outline" size="sm" title={t('auto.misc.photosRoutineChecks')}
                      onClick={() => setExpanded(expanded === m.id ? null : m.id)}
                      data-testid={`machine-expand-${m.tag}`}>
                      {expanded === m.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </Button>
                    <Button variant="outline" size="sm" title={t('auto.misc.qrLabel')}
                      onClick={() => setQrTarget(m)} data-testid={`machine-qr-${m.tag}`}>
                      <QrCode className="w-4 h-4" />
                    </Button>
                    {canEdit && (
                      <Button variant="outline" size="sm" title={t('auto.misc.edit')}
                        onClick={() => { setEditing(m); setForm({ ...EMPTY, ...m }); }}
                        data-testid={`machine-edit-${m.tag}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                    )}
                    {canEdit && (
                      <Button variant="outline" size="sm" title={t('auto.misc.delete')}
                        onClick={() => setDeleteId(m.id)} data-testid={`machine-delete-${m.tag}`}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                </div>

                {expanded === m.id && (
                  <div className="mt-3 pt-3 border-t space-y-4" data-testid={`machine-detail-${m.tag}`}>
                    <div>
                      <p className="text-xs font-medium text-slate-700 flex items-center gap-1.5 mb-2">
                        <Camera className="w-3.5 h-3.5" /> {t('auto.misc.photos')}
                        <span className="font-normal text-slate-500">
                          — the starred one is shown publicly, the rest stay staff-only
                        </span>
                      </p>
                      <EntityPhotoGallery entityType="machine" entityId={m.id} compact
                        readOnly={!canEdit} mainPhotoId={m.main_photo_id}
                        onSelectMain={canEdit ? (pid) => setMainPhoto(m.id, pid) : undefined} />
                    </div>

                    <div>
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                        <p className="text-xs font-medium text-slate-700 flex items-center gap-1.5">
                          <ClipboardCheck className="w-3.5 h-3.5" />
                          {m.checklist_template_name
                            ? `Routine check — ${m.checklist_template_name}`
                            : 'No routine checklist attached'}
                        </p>
                        {canEdit && m.checklist_template_id && (
                          <Button size="sm" variant="outline" onClick={() => newCheck(m)}
                            data-testid={`machine-new-check-${m.tag}`}>
                            <Plus className="w-3.5 h-3.5 mr-1.5" /> {t('auto.misc.newCheck')}
                          </Button>
                        )}
                      </div>
                      {(checks[m.id] || []).length === 0 ? (
                        <p className="text-xs text-slate-500">{t('auto.misc.noChecksLoggedForThisMachine')}</p>
                      ) : (
                        <ul className="divide-y divide-slate-100 border rounded">
                          {(checks[m.id] || []).map((c) => (
                            <li key={c.id} className="px-3 py-2 flex items-center justify-between gap-2">
                              <button type="button" onClick={() => onOpenChecklist?.(c.id)}
                                className="text-left min-w-0 hover:text-[#AA0000] transition-colors"
                                data-testid={`machine-check-${c.number}`}>
                                <span className="text-xs font-medium block break-words">{c.number} — {c.name}</span>
                                <span className="text-[11px] text-slate-500">
                                  {(c.status || '').replace(/_/g, ' ')}
                                  {c.date ? ` · ${String(c.date).slice(0, 10)}` : ''}
                                </span>
                              </button>
                              {c.signed && <Badge className="bg-green-100 text-green-700 text-[11px]">{t('auto.misc.signed')}</Badge>}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) { setEditing(null); setForm(EMPTY); } }}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit {editing?.tag}</DialogTitle></DialogHeader>
          <MachineForm form={form} setForm={setForm} templates={templates} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>{t('auto.misc.cancel')}</Button>
            <Button onClick={save} data-testid="update-machine-btn">{t('auto.misc.saveChanges')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => { if (!o) setDeleteId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('auto.misc.deleteThisMachine')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('auto.misc.itsQrStickerStopsWorkingChecklists')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('auto.misc.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="confirm-machine-delete">{t('auto.misc.delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {qrTarget && (
        <QrLabelDialog open={!!qrTarget} onOpenChange={(o) => { if (!o) setQrTarget(null); }}
          kind="machine" id={qrTarget.id} title={`${qrTarget.tag}${qrTarget.name ? ` — ${qrTarget.name}` : ''}`}
          isAdmin={isAdmin} />
      )}
      {bulkOpen && (
        <QrLabelDialog open={bulkOpen} onOpenChange={setBulkOpen} kind="machine" ids={selected} isAdmin={isAdmin} />
      )}
    </div>
  );
};

export default MachinesPanel;
